import { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Reusable Form Select — compatible with react-hook-form `register()`
 *
 * @param {Array<{value:string,label:string}|string>} options
 */

const SIZES = {
  sm: 'h-8 text-[12px]',
  md: 'h-[35px] text-[13px]',
  lg: 'h-[44px] text-sm',
};

const FormSelect = forwardRef(
  (
    {
      label,
      field,
      placeholder,
      options = [],
      error,
      required = false,
      disabled = false,
      icon,
      size = 'md',
      className = '',
      labelClassName = '',
      inputClassName = '',
      hideError = false,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={`space-y-1.5 ${className}`}>
        {/* Label */}
        {label && (
          <label
            htmlFor={field}
            className={`flex items-center gap-1.5 text-[13px] font-normal text-slate-500 ${labelClassName}`}
          >
            {icon && <span className="text-slate-400 flex-shrink-0">{icon}</span>}
            {label}
            {required && <span className="text-red-500 text-xs">*</span>}
          </label>
        )}

        {/* Select wrapper */}
        <div className="relative">
          <select
            ref={ref}
            id={field}
            name={field}
            disabled={disabled}
            className={[
              'w-full border rounded-md px-3 pr-9 appearance-none',
              'text-slate-800 cursor-pointer',
              'focus:outline-none focus:ring-1 transition-all bg-white',
              SIZES[size],
              error
                ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                : 'border-slate-200 focus:border-brand-blue focus:ring-brand-blue/30',
              disabled && 'bg-slate-100 cursor-not-allowed opacity-60',
            ]
              .filter(Boolean)
              .join(' ')}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {children}
            {options.map((opt, i) => {
              const value = typeof opt === 'object' ? (opt._id ?? opt.id ?? opt.value ?? i) : opt;
              let optLabel = typeof opt === 'object' ? (opt.label || opt.title || opt.name) : opt;
              
              // If label is missing or is an object, try other common fields
              if (typeof optLabel === 'object' && optLabel !== null) {
                optLabel = optLabel.vendorName || optLabel.categoryName || optLabel.brandName || optLabel.unitName || optLabel.itemName || optLabel.storeName || optLabel.registeredTypeName || optLabel.name || optLabel.title || "—";
              }
              
              // Final fallback to string
              const safeLabel = (optLabel === undefined || optLabel === null) ? "—" : String(optLabel);
              
              return (
                <option key={value} value={value}>
                  {safeLabel}
                </option>
              );
            })}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>

        {/* Error */}
        {error && (
          <p className="text-[12px] text-red-500 font-medium flex items-center gap-1">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </p>
        )}
      </div>
    );
  },
);

FormSelect.displayName = 'FormSelect';
export default FormSelect;
